import { Stack } from '@wegroup/design-system';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocale } from '../../../../../../../i18n';
import {
  FailedLouiseQuote,
  LouiseQuote,
  ProductWithQuality,
} from '../../../../../../../types/Quotes';
import StrokeBox from '../../../../../../dashboard/src/components/common/StrokeBox';
import useSessionId from '../../../../../../flows/src/hooks/useSessionId';
import {
  getOfferForProductAndQuote,
  OfferState,
} from '../../../../services/offersService';
import {
  QuoteSpecifications,
  updateLoadedProductIds,
  updateShownProducts,
} from '../../../../store/redux/slices/offersSlice';
import { ProductInfoAndContent } from '../../api/getProducts';
import { useQuote } from '../../api/getQuote';
import SingleOfferStateProvider from '../../store/offer/SingleOfferStateProvider';
import useSelectedQuotesStore from '../../store/zustand/selectedQuotes';
import OfferActions, {
  EnabledOfferActions,
} from './offer-actions/OfferActions';
import OfferOverview from './offer-overview/OfferOverview';
import OfferResult from './offer-result/OfferResult';

interface Props {
  product: ProductWithQuality;
  productContentItems: ProductInfoAndContent[];
  enabledActions?: EnabledOfferActions[];
  /** When true, the quote will be stored in selectedQuotesStore instead */
  isSelectable?: boolean;
  isHidden?: boolean;
}

const SingleOffer: React.FC<Props> = ({
  product,
  productContentItems,
  enabledActions = ['COMPARE'],
  isSelectable,
  isHidden,
}) => {
  const dispatch = useDispatch();
  const { userLang } = useLocale();
  const sessionId = useSessionId();

  const [selectedGuarantees, setSelectedGuarantees] = useState<string[]>(
    product.selected_guarantees,
  );
  const [quoteSpecs, setQuoteSpecs] = useState<QuoteSpecifications>({
    save_quote: true,
  });

  const { data, refetch, isFetching } = useQuote({
    product_id: product.hash_id,
    quoteUrl: product.self,
    selectedGuarantees,
    quoteSpecs,
    sessionId,
    lang: userLang,
  });

  const quote = data as LouiseQuote | FailedLouiseQuote | undefined;

  const offer = getOfferForProductAndQuote(product, quote, true, isFetching);

  const isSelected = useSelectedQuotesStore((state) =>
    state.selectedQuotes.some(({ id }) => id === offer.id),
  );

  useEffect(() => {
    if (offer?.state === OfferState.FETCHED) {
      const prodObject = {
        hashId: offer.hash_id,
        price: offer.premium?.total_premium,
        quality: offer.matching_score,
      };
      dispatch(updateShownProducts(prodObject));
    }
    if (
      offer?.state === OfferState.ERROR ||
      offer?.state === OfferState.FETCHED
    ) {
      dispatch(updateLoadedProductIds(offer?.hash_id));
    }
  }, [offer.state]);

  const productContent = productContentItems.find(
    ({ insurance_company, insurance_type, insurance_name }) =>
      insurance_company === product.info.company.key &&
      insurance_type === product.info.insurance_type &&
      insurance_name === product.info.insurance_name,
  );

  if (!productContent) {
    console.error(
      `Product content not found, hiding offer. Please make sure there is content for the product with insurance company: '${product.info.company.key}' and  insurance type: '${product.info.insurance_type}'`,
    );
    return null;
  }

  if (
    offer?.state === OfferState.ERROR &&
    offer?.info.insurance_type === 'boat'
  ) {
    return null;
  }
  return (
    <SingleOfferStateProvider
      offer={offer}
      productContent={productContent}
      quoteSpecs={quoteSpecs}
      setQuoteSpecs={setQuoteSpecs}
      selectedGuarantees={selectedGuarantees}
      setSelectedGuarantees={setSelectedGuarantees}
      fetchQuote={refetch}
      isFetching={isFetching}
    >
      <StrokeBox
        p="0"
        overflow="hidden"
        borderColor={isSelected ? 'primary.500' : 'gray.strokeAndBg'}
        transition="0.2s"
        display={isHidden ? 'none' : undefined}
      >
        <Stack
          flexDirection={{
            base: 'column-reverse',
            tablet: 'row',
          }}
          spacing="0"
          align="stretch"
        >
          <OfferOverview documents={product.documents} />
          <OfferResult />
        </Stack>
        <OfferActions
          enabledActions={enabledActions}
          isSelectable={isSelectable}
        />
      </StrokeBox>
    </SingleOfferStateProvider>
  );
};

export default SingleOffer;
